/**
 * First-party signup attribution capture — reads the website-forwarded
 * marketing params (eva_vid/eva_sid/UTMs/ad click IDs) once on /signup's
 * first render and persists them in sessionStorage so they survive company
 * lookup, validation errors, and the Microsoft OAuth redirect round-trip.
 *
 * See docs/eva-insights-signup-attribution-lifecycle-ticket.md's "Website to
 * Eva Insights Contract" for the full param list, and eva-website's
 * src/utils/tracking.js (buildAttributedUrl) for what forwards these.
 */

const STORAGE_KEY = 'eva_signup_attribution';

// Deliberately separate from STORAGE_KEY/ATTRIBUTION_PARAMS below: the `d`
// param is an HMAC-signed, PII-carrying campaign-link token (email/name --
// see eva-gtm-platform's click_tracking.py/roi_calculator.py), not part of
// the non-PII marketing-attribution contract those carry. It's forwarded
// as-is to the backend (services/click_tracking_token.py) for server-side
// signature verification -- this file never decodes or trusts it itself.
const CLICK_TOKEN_STORAGE_KEY = 'eva_signup_click_token';

// The exact website-contract query param names this app must read on /signup.
const ATTRIBUTION_PARAMS = [
  'eva_vid',
  'eva_sid',
  'eva_cta',
  'eva_landing_host',
  'eva_landing_page',
  'eva_referrer',
  // The CRM contact_id eva-website's tracking.js forwards from a campaign
  // link's known_contact_id -- not PII (an opaque CRM row id), used by
  // EIP-415's conversion webhook to report a trial signup back to
  // eva-gtm-platform against this specific contact.
  'eva_contact_id',
  'ref',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'utm_id',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'msclkid',
  'li_fat_id',
  'hsa_cam',
  'hsa_grp',
  'hsa_ad',
];

/** Parses the website-contract params out of a URLSearchParams instance. */
function extractAttributionParams(searchParams) {
  const attribution = {};
  ATTRIBUTION_PARAMS.forEach((param) => {
    const value = searchParams.get(param);
    if (value) attribution[param] = value;
  });
  return attribution;
}

/**
 * Captures attribution params from the current URL and merges them into
 * sessionStorage, preserving any already-captured value not present on this
 * particular navigation (e.g. a validation-error re-render with no query
 * string at all shouldn't wipe out what was captured on first load).
 * Returns the merged attribution object.
 */
export function captureSignupAttribution(searchParams) {
  const fromUrl = extractAttributionParams(searchParams);
  let stored = {};
  try {
    stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch {
    stored = {};
  }
  const merged = { ...stored, ...fromUrl };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // sessionStorage unavailable (private browsing, etc.) — attribution is
    // best-effort, not a hard requirement for signup to work.
  }
  return merged;
}

/** Reads the currently persisted attribution object without re-parsing the URL. */
export function getSignupAttribution() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

/**
 * Maps the persisted attribution's raw website-contract param names
 * (eva_landing_host, eva_cta, utm_campaign, ...) onto GA4's allowed field
 * names (landing_host, cta, utm_campaign, ...) -- see ga4.js's ALLOWED_FIELDS.
 * Also injects visitor_id/session_id, since GA4 events need those under
 * their own (non "eva_"-prefixed) names too.
 */
export function getGa4AttributionFields() {
  const attribution = getSignupAttribution();
  return {
    visitor_id: attribution.eva_vid || '',
    session_id: attribution.eva_sid || '',
    landing_host: attribution.eva_landing_host || '',
    cta: attribution.eva_cta || '',
    utm_source: attribution.utm_source || '',
    utm_medium: attribution.utm_medium || '',
    utm_campaign: attribution.utm_campaign || '',
    contact_id: attribution.eva_contact_id || '',
  };
}

/**
 * Captures the raw `?d=` click-tracking token (if present) into sessionStorage,
 * same survive-across-the-funnel reasoning as captureSignupAttribution. Call
 * this alongside captureSignupAttribution on /signup's mount.
 */
export function captureClickToken(searchParams) {
  const token = searchParams.get('d');
  if (!token) return;
  try {
    sessionStorage.setItem(CLICK_TOKEN_STORAGE_KEY, token);
  } catch {
    // Best-effort -- auto-verify is a convenience, not a hard requirement.
  }
}

/** Reads the persisted click-tracking token, or '' if none was captured. */
export function getClickToken() {
  try {
    return sessionStorage.getItem(CLICK_TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

/** Clears the persisted click token — one-time use, so clear it once it's
 * been submitted with a signup attempt regardless of outcome. */
export function clearClickToken() {
  try {
    sessionStorage.removeItem(CLICK_TOKEN_STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

/** Clears persisted attribution — call after a successful signup so a shared/
 * reused browser doesn't reattribute a later, unrelated signup. */
export function clearSignupAttribution() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore.
  }
}

/**
 * Fires a best-effort, fire-and-forget beacon to the public
 * POST /auth/signup-lifecycle-event endpoint (signup_viewed, signup_started,
 * signup_validation_error, signup_submit_error, etc.). Uses
 * navigator.sendBeacon (survives page navigation, doesn't block/await)
 * rather than the shared axios `api` client -- this is one-way telemetry,
 * not a request whose response the page needs, and keeping it off `api`
 * avoids entangling it with that client's auth-header/error interceptors.
 * Never throws: a beacon failing must never surface as a signup-page error.
 */
export function recordSignupLifecycleEvent(eventName, extra = {}) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const url = `${backendUrl}/api/auth/signup-lifecycle-event`;
  const payload = JSON.stringify({
    event_name: eventName,
    attribution: getSignupAttribution(),
    ...extra,
  });
  try {
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch {
    // Fall through to fetch.
  }
  try {
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Beacon is best-effort -- never let telemetry break the signup page.
  }
}
